import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, BeakerIcon, GraphIcon, TerminalIcon, PulseIcon } from '@threatlens/design-system';
import {
  api,
  FAILED_TASK_STATES,
  ApiError,
  getSession,
  type RunConfig,
  type SubmitResponse,
  type TaskStatus,
} from '../lib/api.js';
import { Seo } from '../components/Seo.js';
import { ApiQuickstart } from '../components/home/ApiQuickstart.js';
import { HomeHero } from '../components/home/HomeHero.js';
import { LiveStats } from '../components/home/LiveStats.js';
import { HomeFaq } from '../components/home/HomeFaq.js';
import { RecentAnalyses } from '../components/home/RecentAnalyses.js';
import { CapabilityGrid, ClosingCta, HowItWorks, UseCases } from '../components/home/HomeSections.js';
import { useCybleVisionUpgrading } from '../lib/features.js';
import { errMsg } from '../lib/errors.js';
import { sha256Hex, toBase64, queueWaitLabel } from '../lib/hash.js';
import { formatBytes } from '../lib/format.js';
import { setPendingLiveSample } from '../lib/handoff.js';
import { scanLinkFor } from '../lib/scan-link.js';
import { inferGuestOs } from '../lib/guest-os.js';
import { Link } from 'react-router-dom';

type Stage = 'idle' | 'hashing' | 'submitting' | 'done' | 'error';
type Mode = 'file' | 'url' | 'hash' | 'api';

export function SubmitPage() {
  const cybleUpgrading = useCybleVisionUpgrading();
  const [mode, setMode] = useState<Mode>('file');
  const [stage, setStage] = useState<Stage>('idle');
  const [fileInfo, setFileInfo] = useState<{ name: string; size: number; sha256: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [url, setUrl] = useState('');
  const [hash, setHash] = useState('');
  const [taskStatus, setTaskStatus] = useState<TaskStatus | null>(null);
  const [pendingTaskId, setPendingTaskId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // The task poll below runs for up to 10 minutes. Without this the loop kept issuing requests
  // after the user navigated away and then called nav() from a stale closure, yanking them off
  // whatever page they had moved to.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const nav = useNavigate();
  const authed = !!getSession().accessToken;

  // Run configuration (spec section L). Anonymous callers are forced public + non-interactive.
  // Network defaults to Full internet (controlled real egress behind the host-side guardrail
  // chain), matching the platform's real-egress-by-default direction; all options stay selectable.
  const [cfg, setCfg] = useState<RunConfig>({
    visibility: 'public',
    network: 'full',
    interactive: false,
    captchaToken: authed ? undefined : 'dev-captcha-ok',
  });

  // useMemo, not a bare object literal: the anonymous branch builds a NEW object every render, so the
  // two useCallbacks that depend on it were being rebuilt every render too - which quietly defeats
  // their memoisation and makes any child taking them as props re-render as well.
  const effectiveCfg: RunConfig = useMemo(
    () =>
      authed
        ? cfg
        : { ...cfg, visibility: 'public', interactive: false, captchaToken: cfg.captchaToken ?? 'dev-captcha-ok' },
    [authed, cfg],
  );

  const followTask = useCallback(
    async (submission: SubmitResponse) => {
      setPendingTaskId(submission.taskId);
      if (submission.state === 'completed') {
        setStage('done');
        nav(`/report/${submission.contentHash}`);
        return;
      }
      for (let attempt = 0; attempt < 600; attempt++) {
        if (!mounted.current) return;
        const task = await api.task(submission.taskId);
        if (!mounted.current) return;
        setTaskStatus(task);
        if (task.state === 'completed' && task.reportReady) {
          setStage('done');
          nav(`/report/${task.contentHash}`);
          return;
        }
        if (FAILED_TASK_STATES.has(task.state)) {
          throw new Error(task.error ?? `Analysis ${task.state}`);
        }
        await new Promise((resolve) => setTimeout(resolve, 1_000));
      }
      // Long queue/detonation backlog: hand off to the dedicated live watcher instead of dead-ending.
      throw new Error('__WATCH__');
    },
    [nav],
  );

  const handleFile = useCallback(
    async (file: File) => {
      try {
        setError(null);
        // Guard before reading the whole file into memory (arrayBuffer + base64) - a multi-hundred-MB
        // drop would otherwise freeze/OOM the tab. Server cap is 32 MiB, so reject early + friendly.
        const MAX_FILE_BYTES = 32 * 1024 * 1024;
        if (file.size > MAX_FILE_BYTES) {
          setStage('error');
          setError(`File is too large (${formatBytes(file.size)}). The maximum is 32 MiB.`);
          return;
        }
        setStage('hashing');
        const buf = await file.arrayBuffer();
        const sha = await sha256Hex(buf);
        setFileInfo({ name: file.name, size: file.size, sha256: sha });
        const base64 = toBase64(buf);
        // Interactive "Live VM session": hand the sample to the live page and launch a real VM
        // there, rather than routing it through the automated pipeline (which never opens a VM).
        if (effectiveCfg.interactive) {
          setPendingLiveSample({
            fileName: file.name,
            fileBase64: base64,
            network: effectiveCfg.network,
            os: inferGuestOs(buf, file.name),
          });
          nav('/live');
          return;
        }
        setStage('submitting');
        setTaskStatus(null);
        const res = await api.submitFile(base64, file.name, effectiveCfg);
        await followTask(res);
      } catch (e) {
        setStage('error');
        setError(errMsg(e));
      }
    },
    [effectiveCfg, followTask, nav],
  );

  const handleUrlOrHash = useCallback(async () => {
    // A URL is a website SCAN, not a file detonation. Hand off to the dedicated URL scanner page
    // (urlscan.io superset) which streams the two-tier result. Hashes stay on the lookup path.
    if (mode === 'url') {
      if (!url.trim()) return;
      nav(scanLinkFor(url.trim()));
      return;
    }
    try {
      setError(null);
      setStage('submitting');
      setTaskStatus(null);
      const res = await api.submitHash(hash.trim(), effectiveCfg);
      await followTask(res);
    } catch (e) {
      // Not in our corpus: don't dead-end on "No analysis on file". Hand off to the report page,
      // which queries our threat network for a PRELIMINARY verdict (and kicks off analysis in the
      // background) - the same just-in-time lookup a direct /report/<hash> visit gets.
      if (e instanceof ApiError && e.status === 404) {
        nav(`/report/${encodeURIComponent(hash.trim().toLowerCase())}`);
        return;
      }
      setStage('error');
      setError(errMsg(e));
    }
  }, [mode, url, hash, effectiveCfg, followTask, nav]);

  return (
    <div className="mx-auto max-w-6xl">
      <Seo
        title="Malware Analysis and URL Scanning"
        description="Analyze any file or URL for free. Multi-engine static scanning, real sandbox detonation with behavior capture, interactive live-VM analysis, URL and phishing analysis, and a queryable threat-intelligence graph."
        canonicalPath="/"
      />
      <HomeHero cybleUpgrading={cybleUpgrading} />
      <LiveStats />

      {/* The submit tool. `scroll-mt` keeps the heading clear of the sticky header when the hero's
          "Analyze a file or URL" anchor jumps here. */}
      <div id="submit" className="mb-3 mt-10 flex scroll-mt-20 items-end justify-between">
        <h2 className="text-h3 font-bold text-text-primary">Submit for analysis</h2>
        <span className="hidden text-micro text-text-muted sm:block">Anonymous submissions are public · 32&nbsp;MiB max</span>
      </div>

      <div className="mb-3 flex gap-6 border-b border-border-subtle" role="tablist">
        {([['file', 'File'], ['url', 'URL'], ['hash', 'Hash'], ['api', 'API']] as [Mode, string][]).map(([m, label]) => (
          <button
            key={m}
            role="tab"
            aria-selected={mode === m}
            onClick={() => {
              setMode(m);
              setStage('idle');
              setError(null);
            }}
            className={`-mb-px border-b-2 px-1 py-2.5 text-sm font-semibold transition-[color] ${
              mode === m
                ? 'border-accent-500 text-text-primary'
                : 'border-transparent text-text-muted hover:text-text-primary'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === 'api' ? (
        // Same component the hero renders, so the published commands cannot drift between the two
        // places we show them.
        <ApiQuickstart />
      ) : mode === 'file' ? (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) void handleFile(f);
          }}
          className={`rounded-md border-2 border-dashed bg-bg-raised p-4 text-center transition-colors sm:p-5 ${
            dragging ? 'border-accent-500 bg-accent-500/10' : 'border-neutral-micro'
          }`}
        >
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-accent-500/10 text-accent-500">
            <BeakerIcon size={22} />
          </div>
          {stage === 'idle' || stage === 'error' ? (
            <>
              <p className="text-text-primary">Drop a file, or paste a URL, IP, domain, or hash.</p>
              <p className="mt-1 text-sm text-text-muted">or</p>
              <Button className="mt-2 text-body font-semibold" onClick={() => inputRef.current?.click()}>
                Choose file
              </Button>
              <input
                ref={inputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
              <p className="mt-2 text-xs text-text-muted">
                Anonymous submissions are public and capped at 32&nbsp;MiB. Sign in for private analysis.
              </p>
            </>
          ) : (
            <div className="text-left">
              <ProgressRow active={stage === 'hashing'} done={stage !== 'hashing'} label="Hashing locally (SHA-256)" />
              {fileInfo && (
                <p className="my-2 break-all font-mono text-xs text-text-muted">
                  {fileInfo.name} · {formatBytes(fileInfo.size)} · {fileInfo.sha256.slice(0, 32)}…
                </p>
              )}
              <QueueBanner task={taskStatus} />
              <ProgressRow
                active={stage === 'submitting' && !taskStatus?.staticVerdict && taskStatus?.state !== 'queued'}
                done={!!taskStatus?.staticVerdict || stage === 'done'}
                label="Static analysis"
              />
              <ProgressRow
                active={
                  stage === 'submitting' &&
                  !!taskStatus?.staticVerdict &&
                  taskStatus.state !== 'completed'
                }
                done={stage === 'done'}
                label={
                  taskStatus?.state === 'queued-dynamic'
                    ? 'Dynamic analysis · waiting for a detonation slot'
                    : `Dynamic analysis${taskStatus?.progress ? ` · ${taskStatus.progress.pct}%` : ''}`
                }
              />
            </div>
          )}
          <SubmitError error={error} taskId={pendingTaskId} />
        </motion.div>
      ) : (
        <div className="rounded-md border border-border-subtle bg-bg-raised p-6">
          <label className="mb-2 block text-sm font-medium text-text-secondary">
            {mode === 'url' ? 'URL to scan' : 'Hash to look up (sha256/sha1/md5)'}
          </label>
          <input
            value={mode === 'url' ? url : hash}
            onChange={(e) => (mode === 'url' ? setUrl(e.target.value) : setHash(e.target.value))}
            onKeyDown={(e) => { if (e.key === 'Enter') void handleUrlOrHash(); }}
            placeholder={mode === 'url' ? 'https://suspicious.example/payload' : 'e44...'}
            className="w-full rounded-md border border-border-default bg-bg-base px-3 py-2 font-mono text-sm text-text-primary outline-none focus:border-accent-500"
          />
          <Button className="mt-4 text-body font-semibold" onClick={() => void handleUrlOrHash()} disabled={stage === 'submitting'}>
            {stage === 'submitting' ? 'Submitting…' : mode === 'url' ? 'Scan URL' : 'Look up hash'}
          </Button>
          {mode === 'url' && (
            <p className="mt-2 text-xs text-text-muted">
              URLs are analyzed by the two-tier URL scanner (static fast pass + dynamic browser), not the file sandbox.
            </p>
          )}
          {stage === 'submitting' && taskStatus && (
            <div className="mt-4">
              <QueueBanner task={taskStatus} />
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-bg-inset">
                <div
                  className="h-full bg-accent-500 transition-all"
                  style={{ width: `${taskStatus.progress?.pct ?? 2}%` }}
                />
              </div>
              <p className="mt-2 text-xs text-text-muted">
                {taskStatus.state}
                {taskStatus.staticVerdict ? ` · static: ${taskStatus.staticVerdict.verdict}` : ''}
              </p>
            </div>
          )}
          <SubmitError error={error} taskId={pendingTaskId} />
        </div>
      )}

      {/* Run configuration applies to a submission made through this form; the API tab is documentation
          and carries its own options in the request body, so showing the panel there would imply the
          controls affect the printed commands. */}
      {mode !== 'api' && <RunConfigPanel cfg={cfg} setCfg={setCfg} authed={authed} />}

      <div className="mt-6">
        <p className="mb-2 text-micro font-semibold uppercase tracking-wider text-text-muted">
          Every submission runs the full pipeline
        </p>
        <div className="grid grid-cols-1 divide-y divide-border-subtle overflow-hidden rounded-md border border-border-subtle sm:grid-cols-2 sm:divide-x lg:grid-cols-4 lg:divide-y-0">
          <PipelineStep n="01" icon={<PulseIcon />} title="Static" desc="Multi-engine, YARA, capa, packers, IOCs in <5s (Cyble internal, unaudited)." />
          <PipelineStep n="02" icon={<BeakerIcon />} title="Dynamic" desc="Concurrent full-system detonation with full behavior capture." />
          <PipelineStep n="03" icon={<TerminalIcon />} title="Interactive" desc="Live desktop; drive the sample in real time." />
          <PipelineStep n="04" icon={<GraphIcon />} title="Graph" desc="Pivot across every related IOC and campaign." />
        </div>
      </div>

      <CapabilityGrid />
      <HowItWorks />
      <RecentAnalyses />
      <UseCases />
      <HomeFaq />
      <ClosingCta />
    </div>
  );
}

function RunConfigPanel({
  cfg,
  setCfg,
  authed,
}: {
  cfg: RunConfig;
  setCfg: (c: RunConfig) => void;
  authed: boolean;
}) {
  return (
    <div className="mt-4 rounded-lg border border-border-subtle bg-bg-raised p-4">
      <h3 className="mb-3 text-sm font-semibold text-text-primary">Run configuration</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        <Select
          label="Visibility"
          value={authed ? cfg.visibility : 'public'}
          disabled={!authed}
          onChange={(v) => setCfg({ ...cfg, visibility: v as RunConfig['visibility'] })}
          options={[
            ['public', 'Public (shareable)'],
            ['private', 'Private (account)'],
          ]}
        />
        <Select
          label="Network"
          value={cfg.network}
          onChange={(v) => setCfg({ ...cfg, network: v as RunConfig['network'] })}
          options={[
            ['simulated', 'Simulated (INetSim)'],
            ['full', 'Full internet'],
            ['none', 'No network'],
          ]}
        />
        <Select
          label="Guest OS"
          value={cfg.os ?? 'auto'}
          onChange={(v) => setCfg({ ...cfg, os: v as RunConfig['os'] })}
          options={[
            ['auto', 'Auto (from file)'],
            ['windows', 'Windows'],
            ['linux', 'Linux'],
            ['android', 'Android'],
            // There is no macOS full-system sandbox on the fleet, so a Mach-O sample receives static
            // analysis and lightweight emulation only. Offering a bare "macOS" implied a detonation
            // that cannot happen, and because an explicit OS is a DIRECTIVE (it suppresses the
            // cross-OS second leg), picking it actively made the result worse. Say so in the option.
            ['macos', 'macOS (static only - no macOS sandbox)'],
          ]}
        />
        <Select
          label="Sandbox"
          value={cfg.engine ?? 'auto'}
          onChange={(v) => setCfg({ ...cfg, engine: v })}
          options={[
            ['auto', 'Auto (multi-engine)'],
            ['cape', 'CAPE (Windows)'],
            ['qemu', 'QEMU/KVM full-system'],
            ['drakvuf', 'DRAKVUF (VMI)'],
            ['droidy', 'Droidy (Android)'],
            ['emulator', 'Qiling emulation'],
          ]}
        />
        <div>
          <span className="mb-1 block text-xs font-medium text-text-muted">Interactive</span>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border-default bg-bg-base px-3 py-2 text-sm text-text-primary">
            <input
              type="checkbox"
              disabled={!authed}
              checked={authed && cfg.interactive}
              onChange={(e) => setCfg({ ...cfg, interactive: e.target.checked })}
            />
            Live VM session
          </label>
        </div>
      </div>
      {!authed && (
        <p className="mt-3 text-xs text-text-muted">
          Sign in to unlock private submissions and interactive live-VM sessions.
        </p>
      )}
    </div>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: [string, string][];
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-text-muted">{label}</label>
      <select
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-border-default bg-bg-base px-3 py-2 text-sm text-text-primary outline-none focus:border-accent-500 disabled:opacity-50"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Live queue/wait banner shown while a task waits in the admission queue or for a detonation slot. */
function QueueBanner({ task }: { task: TaskStatus | null }) {
  const label = task ? queueWaitLabel(task) : null;
  if (!label) return null;
  return (
    <div
      className="my-2 flex items-center gap-2 rounded-md border border-accent-500/30 bg-accent-500/10 px-3 py-2 text-sm text-text-primary"
      aria-live="polite"
    >
      <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-accent-500 border-t-transparent motion-reduce:animate-none" />
      <span>{label}</span>
    </div>
  );
}

/** Renders a submit error, upgrading the long-queue sentinel into a "keep watching" hand-off link. */
function SubmitError({ error, taskId }: { error: string | null; taskId: string | null }) {
  if (!error) return null;
  if (error === '__WATCH__') {
    return (
      <p className="mt-4 text-sm text-text-secondary">
        Still working through the queue.{' '}
        {taskId ? (
          <Link to={`/watch/${taskId}`} className="text-accent-400 hover:underline">
            Keep watching this analysis live
          </Link>
        ) : (
          <Link to="/firehose" className="text-accent-400 hover:underline">
            Open the firehose
          </Link>
        )}
        .
      </p>
    );
  }
  return <p className="mt-4 text-sm text-status-danger">{error}</p>;
}

function ProgressRow({ active, done, label }: { active: boolean; done: boolean; label: string }) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
          done ? 'bg-verdict-clean/20 text-verdict-clean' : active ? 'bg-accent-500/20 text-accent-500' : 'bg-bg-inset text-text-muted'
        }`}
      >
        {done ? '✓' : active ? '•' : '·'}
      </span>
      <span className={done ? 'text-text-primary' : active ? 'text-text-primary' : 'text-text-muted'}>
        {label}
        {active && <span className="ml-2 inline-block h-3 w-3 animate-spin rounded-full border-2 border-accent-500 border-t-transparent align-middle motion-reduce:animate-none" />}
      </span>
    </div>
  );
}

/** One stage in the "full pipeline" strip: a mono step number, a navy glyph, and tight copy. */
function PipelineStep({ n, icon, title, desc }: { n: string; icon: React.ReactNode; title: string; desc: string }) {
  return (
    <div className="bg-bg-raised p-4">
      <div className="flex items-center gap-2">
        <span className="font-mono text-micro font-bold text-accent-500">{n}</span>
        {/* Themed, not brand-navy: navy on a navy-black canvas is invisible, so this icon
            disappeared entirely in four of the six themes. */}
        <span className="text-text-muted">{icon}</span>
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-text-secondary">{desc}</p>
    </div>
  );
}
